import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Copy, FileDown, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { adminDownloadPriceList, adminGetPriceList, adminUpdatePriceList, formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "w-full rounded-md border border-input bg-white/60 px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const headInputCls =
  "mt-1.5 rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition w-full";

const EMPTY_ROW = { product: "", size: "", pages: "", mrp: "", net_rate: "", qty_box: "" };

const iconBtn = "p-2 rounded-full border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors disabled:opacity-30 disabled:hover:bg-transparent";

export const PriceListTab = ({ token }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setForm(await adminGetPriceList(token));
    } catch {
      toast.error("Could not load the price list");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = form?.rows || [];
  const setRows = (updater) => setForm((f) => ({ ...f, rows: typeof updater === "function" ? updater(f.rows) : updater }));

  const updateRow = (i, key, value) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const cloneRow = (i) =>
    setRows((prev) => [...prev.slice(0, i + 1), { ...prev[i] }, ...prev.slice(i + 1)]);

  const moveRow = (i, dir) =>
    setRows((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      setForm(await adminUpdatePriceList(token, form));
      toast.success("Price list saved — the PDF download now uses these rates");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="py-16 flex justify-center" data-testid="pricelist-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl" data-testid="pricelist-tab">
      <div className="bg-white/70 border border-border/60 rounded-xl p-6 space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Title / month</span>
            <input
              data-testid="pl-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="September 2026"
              className={headInputCls}
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Subtitle (under the title)</span>
            <input
              data-testid="pl-subtitle-input"
              value={form.subtitle || ""}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              placeholder="Dealer & Wholesale Rates"
              className={headInputCls}
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Tagline (very bottom)</span>
            <input
              data-testid="pl-tagline-input"
              value={form.tagline || ""}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="Write Green, Live Clean"
              className={headInputCls}
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Footer note (disclaimer line)</span>
            <input
              data-testid="pl-footer-note-input"
              value={form.footer_note || ""}
              onChange={(e) => setForm({ ...form, footer_note: e.target.value })}
              placeholder="Rates are exclusive of GST & freight…"
              className={headInputCls}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Contact line (email · phone · website)</span>
            <input
              data-testid="pl-contact-line-input"
              value={form.contact_line || ""}
              onChange={(e) => setForm({ ...form, contact_line: e.target.value })}
              placeholder="Classina® — A brand of SBK Manufacturing Co. · email · phone · website"
              className={headInputCls}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground max-w-xl">
            Every line and every cell of the PDF is editable here — arrows reorder rows, clone duplicates a row, trash removes it.
            Click <strong>Save</strong> first, then <strong>Download PDF</strong>.
          </p>
          <div className="flex gap-2">
            <button
              data-testid="pl-add-row"
              onClick={addRow}
              className="rounded-full border border-border px-5 py-2.5 text-xs font-bold flex items-center gap-1.5 hover:bg-secondary transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add row
            </button>
            <button
              data-testid="pl-save-button"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-60 hover:-translate-y-0.5 transition-transform"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              data-testid="pl-download-button"
              onClick={() => adminDownloadPriceList(token).catch(() => toast.error("Could not download price list"))}
              className="rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-xs font-bold flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform"
            >
              <FileDown className="w-3.5 h-3.5" /> Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white/70 border border-border/60 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1.4fr_0.7fr_0.7fr_0.9fr_0.8fr_auto] gap-2 px-4 py-3 bg-secondary/50 text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground">
          <span>Product</span>
          <span>Size</span>
          <span>Pages</span>
          <span>MRP (₹)</span>
          <span>Net Rate (₹)</span>
          <span>Qty/Box</span>
          <span className="w-32 text-center">Actions</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} data-testid={`pl-row-${i}`} className="grid grid-cols-[2fr_1.4fr_0.7fr_0.7fr_0.9fr_0.8fr_auto] gap-2 px-4 py-2 border-t border-border/40 items-center">
            <input data-testid={`pl-row-${i}-product`} value={r.product} onChange={(e) => updateRow(i, "product", e.target.value)} placeholder="Product name" className={inputCls} />
            <input data-testid={`pl-row-${i}-size`} value={r.size} onChange={(e) => updateRow(i, "size", e.target.value)} placeholder="18 x 24 cms" className={inputCls} />
            <input data-testid={`pl-row-${i}-pages`} value={r.pages} onChange={(e) => updateRow(i, "pages", e.target.value)} placeholder="72" className={inputCls} />
            <input data-testid={`pl-row-${i}-mrp`} value={r.mrp} onChange={(e) => updateRow(i, "mrp", e.target.value)} placeholder="30" className={inputCls} />
            <input data-testid={`pl-row-${i}-net`} value={r.net_rate} onChange={(e) => updateRow(i, "net_rate", e.target.value)} placeholder="14" className={inputCls} />
            <input data-testid={`pl-row-${i}-qty`} value={r.qty_box} onChange={(e) => updateRow(i, "qty_box", e.target.value)} placeholder="192" className={inputCls} />
            <div className="flex items-center gap-1 w-32 justify-center">
              <button
                data-testid={`pl-up-${i}`}
                onClick={() => moveRow(i, -1)}
                disabled={i === 0}
                className={iconBtn}
                aria-label={`Move row ${i + 1} up`}
                title="Move up"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`pl-down-${i}`}
                onClick={() => moveRow(i, 1)}
                disabled={i === rows.length - 1}
                className={iconBtn}
                aria-label={`Move row ${i + 1} down`}
                title="Move down"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`pl-clone-${i}`}
                onClick={() => cloneRow(i)}
                className={iconBtn}
                aria-label={`Clone row ${i + 1}`}
                title="Clone row"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`pl-delete-${i}`}
                onClick={() => removeRow(i)}
                className="p-2 rounded-full border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                aria-label={`Remove row ${i + 1}`}
                title="Delete row"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">No rows — click "Add row" to start the list.</p>
        )}
      </div>
    </div>
  );
};
